const MAX_DIMENSION = 1600;
const COMPRESSION_QUALITY = 0.82;
const COMPRESSION_THRESHOLD_BYTES = 1024 * 1024; // 1 MB

const COMPRESSIBLE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/bmp',
];

const isCompressibleImage = file => {
  return COMPRESSIBLE_TYPES.includes(file.type);
};

const shouldCompress = file => {
  return isCompressibleImage(file) && file.size > COMPRESSION_THRESHOLD_BYTES;
};

const loadImage = file =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

const calculateDimensions = (width, height, maxDim) => {
  if (width <= maxDim && height <= maxDim) return { width, height };

  const ratio = Math.min(maxDim / width, maxDim / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), mimeType, quality);
  });

/**
 * Compresses an image File using Canvas.
 * - Resizes to fit within MAX_DIMENSION
 * - Re-encodes as JPEG at COMPRESSION_QUALITY
 * - Only processes images > COMPRESSION_THRESHOLD_BYTES
 * - Returns the original file if compression doesn't reduce size
 */
export const compressImage = async file => {
  if (!shouldCompress(file)) return file;

  try {
    const img = await loadImage(file);
    const { width, height } = calculateDimensions(
      img.naturalWidth,
      img.naturalHeight,
      MAX_DIMENSION
    );

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    URL.revokeObjectURL(img.src);

    const outputType = 'image/jpeg';
    const blob = await canvasToBlob(canvas, outputType, COMPRESSION_QUALITY);

    if (blob.size >= file.size) return file;

    const name = file.name.replace(/\.[^.]+$/, '.jpg');
    return new File([blob], name, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
};
